'use client';

import { IS_MOBILE } from '@repo/common-utils';
import { cva } from 'class-variance-authority';
import cn from 'clsx';
import { HTMLAttributes, MouseEvent, ReactNode, useCallback, useEffect, useState } from 'react';

const listContainer = cva('box-content flex w-full items-center rounded-bzc-medium', {
  variants: {
    size: {
      large: '-mx-normal-small px-normal-small py-normal-medium gap-normal-small',
      medium: '-mx-normal-small px-normal-small py-normal-medium gap-normal-small',
      small: '-mx-normal-small p-normal-small gap-normal-small',
      xsmall: '-mx-tiny-large px-tiny-large py-tiny-medium gap-tiny-large'
    },
    state: {
      enabled: 'text-neutral-foreground-main',
      loading: 'text-neutral-foreground-disabled',
      disabled: 'text-neutral-foreground-disabled'
    }
  },
  defaultVariants: {
    size: 'medium'
  }
});

const titleContainer = cva('flex flex-1 flex-col whitespace-pre-wrap min-w-0', {
  variants: {
    size: {
      large: 'heading-small-bold gap-tiny-small',
      medium: 'body-large-semibold gap-tiny-small',
      small: 'body-small-semibold gap-tiny-small',
      xsmall: 'label-xsmall-medium'
    },
    state: {
      enabled: 'text-neutral-foreground-main',
      loading: 'text-neutral-foreground-disabled',
      disabled: 'text-neutral-foreground-disabled'
    }
  },
  defaultVariants: {
    size: 'medium',
    state: 'enabled'
  }
});

const descriptionContainer = cva('whitespace-pre-wrap w-full', {
  variants: {
    size: {
      large: 'body-small-regular',
      medium: 'caption-medium-regular',
      small: 'caption-xsmall-regular',
      xsmall: 'caption-xsmall-regular'
    },
    state: {
      enabled: 'text-neutral-foreground-low',
      loading: 'text-neutral-foreground-disabled',
      disabled: 'text-neutral-foreground-disabled'
    }
  },
  defaultVariants: {
    size: 'medium',
    state: 'enabled'
  }
});

const overlineContainer = cva('whitespace-pre-wrap w-full', {
  variants: {
    size: {
      large: 'caption-medium-regular',
      medium: 'caption-small-regular',
      small: 'caption-xsmall-regular',
      xsmall: 'caption-xsmall-regular'
    },
    state: {
      enabled: 'text-neutral-foreground-low',
      loading: 'text-neutral-foreground-disabled',
      disabled: 'text-neutral-foreground-disabled'
    }
  },
  defaultVariants: {
    size: 'medium',
    state: 'enabled'
  }
});

export interface ListProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  size?: 'large' | 'medium' | 'small' | 'xsmall';
  title: ReactNode;
  state?: 'loading' | 'enabled' | 'disabled';
  overline?: ReactNode;
  className?: string;
  description?: ReactNode;
  primaryAction?: boolean;
  leadingElement?: ReactNode;
  secondaryAction?: ReactNode;
  supportingElement?: ReactNode;
}

export const List = ({
  size,
  title,
  state = 'enabled',
  overline,
  className,
  description,
  primaryAction,
  leadingElement,
  secondaryAction,
  supportingElement,
  onClick,
  ...props
}: ListProps) => {
  const [actionClassName, setActionClassName] = useState('');
  const handleClickList = (event: MouseEvent<HTMLDivElement, globalThis.MouseEvent>) => {
    if (onClick && primaryAction && state === 'enabled') {
      onClick(event);
    }
  };

  const handleClickSecondaryArea = (event: MouseEvent) => {
    if (!primaryAction || state === 'disabled') return;
    event.stopPropagation();
  };

  const getActionClassName = useCallback(() => {
    if (!primaryAction || state === 'disabled' || state === 'loading') return '';

    if (IS_MOBILE) {
      return 'active:bg-neutral-background-hovered&pressed cursor-pointer';
    }
    return 'hover:bg-neutral-background-hovered&pressed cursor-pointer';
  }, [primaryAction, state]);

  useEffect(() => {
    setActionClassName(getActionClassName());
  }, [getActionClassName]);

  return (
    <div className={cn(listContainer({ size, state, className }), actionClassName)} onClick={handleClickList} role="list" {...props}>
      {leadingElement && (
        <div data-testid="listLeadingElement" className="flex shrink-0 items-center">
          {leadingElement}
        </div>
      )}
      <div data-testid="listTitle" className={titleContainer({ size, state })}>
        {overline && (
          <div data-testid="listOverline" className={overlineContainer({ size, state })}>
            {overline}
          </div>
        )}
        <div>{title}</div>
        {description && (
          <div data-testid="listDescription" className={descriptionContainer({ size, state })}>
            {description}
          </div>
        )}
      </div>
      {(secondaryAction || supportingElement) && (
        <div
          className={cn('flex shrink-0 items-center', {
            'gap-tiny-large': size === 'xsmall',
            'gap-normal-small': size !== 'xsmall'
          })}
        >
          {supportingElement && (
            <div data-testid="listSupportingElement" className="body-medium-medium text-neutral-foreground-low">
              {supportingElement}
            </div>
          )}
          {secondaryAction && (
            <div data-testid="listSecondaryAction" onClick={handleClickSecondaryArea} className={cn({ 'hover:cursor-pointer': !!secondaryAction })}>
              {secondaryAction}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
